/**
 * A microtask scheduler that CANNOT starve the event loop.
 *
 * Deferring work to a microtask is the right call when the work must land
 * after the current synchronous dispatch but before paint (see
 * `useSmartBarPlacement` — it mutates Monaco view zones and must not do so
 * inside Monaco's own render pass, upstream vscode#196669). The failure mode
 * it invites is that microtasks are drained to exhaustion BEFORE the event
 * loop gets another turn: if the deferred work re-triggers whatever scheduled
 * it, the chain never ends, and the renderer hard-freezes — no paint, no
 * input, force-quit only. That is exactly what the inline Execute|Peek bar
 * did on 2026-09-08 (Jeremy's freeze report): view-zone insert → synchronous
 * `onDidScrollChange` → re-run → view-zone remove → synchronous scroll → …
 *
 * A feedback loop like that should be fixed at its source (and that one was),
 * but "we found the loop" is not a structural guarantee that the next one
 * cannot freeze the app. This scheduler is that guarantee: a chain of
 * microtask runs is capped, and once the cap is hit the remaining work is
 * pushed onto a macrotask, which hands the browser a turn to paint and to
 * process input before the work resumes. A live loop becomes a visible
 * stutter (plus a `onChainBroken` report) instead of a dead window.
 *
 * `setTimeout(…, 0)` — not `requestAnimationFrame` — is the macrotask
 * boundary on purpose: rAF does not fire in a hidden tab or a
 * `display: none`d editor, precisely the states in which Jam keeps
 * background workspace tabs mounted.
 */

export interface BoundedMicrotaskScheduler {
  /** Request a run. Bursts inside one tick coalesce into a single run. */
  schedule: () => void;
  /** Cancel everything pending and make further `schedule()` calls no-ops. */
  dispose: () => void;
}

interface BoundedMicrotaskSchedulerOptions {
  /**
   * How many runs may chain back-to-back within a single microtask drain
   * before the scheduler forces a macrotask boundary.
   */
  maxChainedRuns: number;
  /**
   * Called once per broken chain, with the number of chained runs that
   * triggered the break. Use it to report the loop — the scheduler itself
   * only breaks the freeze, it cannot fix the feedback loop that caused it.
   */
  onChainBroken?: (chainedRuns: number) => void;
}

export function createBoundedMicrotaskScheduler(
  run: () => void,
  { maxChainedRuns, onChainBroken }: BoundedMicrotaskSchedulerOptions,
): BoundedMicrotaskScheduler {
  let disposed = false;
  /** A microtask is queued and has not run yet — further requests coalesce. */
  let microtaskQueued = false;
  /** Runs performed since the last macrotask boundary. */
  let chainedRuns = 0;
  /** Armed when a chain starts; its firing IS the macrotask boundary. */
  let chainResetTimer: ReturnType<typeof setTimeout> | null = null;
  /** Armed when a chain is broken; carries the run the chain refused. */
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  const clearChainResetTimer = () => {
    if (chainResetTimer !== null) {
      clearTimeout(chainResetTimer);
      chainResetTimer = null;
    }
  };

  const performRun = () => {
    chainedRuns += 1;
    if (chainResetTimer === null) {
      // The chain is only "chained" until the event loop turns over; anything
      // after that boundary is a fresh, legitimate run rather than feedback.
      chainResetTimer = setTimeout(() => {
        chainResetTimer = null;
        chainedRuns = 0;
      }, 0);
    }
    run();
  };

  const breakChain = () => {
    if (fallbackTimer !== null) return; // one fallback per broken chain
    onChainBroken?.(chainedRuns);
    fallbackTimer = setTimeout(() => {
      fallbackTimer = null;
      // The boundary has been crossed: this run starts a new chain, so the
      // work still settles — it just paid a frame for the privilege.
      clearChainResetTimer();
      chainedRuns = 0;
      if (disposed) return;
      performRun();
    }, 0);
  };

  const schedule = () => {
    if (disposed) return;
    if (chainedRuns >= maxChainedRuns) {
      // Do NOT queue another microtask — that is the freeze.
      breakChain();
      return;
    }
    if (microtaskQueued) return; // coalesce event bursts into one run
    microtaskQueued = true;
    queueMicrotask(() => {
      microtaskQueued = false;
      if (disposed) return;
      performRun();
    });
  };

  const dispose = () => {
    disposed = true;
    clearChainResetTimer();
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  return { schedule, dispose };
}
